import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import { api } from "../lib/api";
import { fmtBRL, fmtNumber, fmtDateShort } from "../lib/format";
import { computeDelta } from "../lib/delta";
import { useFilters, periodToDates } from "../lib/filters";
import { KPIStatCard } from "../components/KPIStatCard";
import { ChartCard } from "../components/ChartCard";
import { PageHeader } from "../components/PageHeader";
import { LoadMoreBar, useProgressiveList } from "../components/LoadMoreRow";
import { IconTruck } from "../components/icons";
import type { TransportCteDashboardDto } from "@bi/types";

const MEMORY_CARDS: Record<string, string> = {
  "cte-receita-mes":    "cte-receita-mes",
  "cte-count-mes":      "cte-count-mes",
  "cte-icms-mes":       "cte-icms-mes",
  "cte-pedagio-mes":    "cte-pedagio-mes",
  "cte-mercadoria-mes": "cte-mercadoria-mes",
  "cte-quantidade-mes": "cte-quantidade-mes",
  "cte-peso-mes":       "cte-peso-mes",
};

function MemoryCardWrapper({
  cardKey,
  children,
  navigate,
}: {
  cardKey: string;
  children: React.ReactNode;
  navigate: (path: string) => void;
}) {
  const hasMemory = Boolean(MEMORY_CARDS[cardKey]);
  if (hasMemory) {
    return (
      <div
        className="cursor-pointer"
        onClick={() => navigate(`/operacional/memoria/${cardKey}`)}
        title="Clique para ver a memória de cálculo"
      >
        {children}
      </div>
    );
  }
  return (
    <div className="cursor-default" title="Memória de cálculo em desenvolvimento">
      {children}
    </div>
  );
}

export function OperationalPage() {
  const navigate = useNavigate();
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  const { data: cte, isLoading } = useQuery({
    queryKey: ["transport", "cte", from, to, filters.comparison],
    queryFn: () =>
      api.get<TransportCteDashboardDto>(
        `/dashboards/transport/cte?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
      ).catch(() => null),
    staleTime: 60_000,
  });

  const s = cte?.summary;
  const p = cte?.prevSummary;

  // ── Deltas ────────────────────────────────────────────────────────────────
  const receitaDelta   = computeDelta(s?.totalValue,      p?.totalValue,      { direction: "higher_is_better" });
  const countDelta     = computeDelta(s?.count,           p?.count,           { direction: "higher_is_better" });
  const pedagioDelta   = computeDelta(s?.tollValue,       p?.tollValue,       { direction: "higher_is_worse"  });
  const icmsDelta      = computeDelta(s?.totalIcms,       p?.totalValue && p.totalValue > 0
    ? (s?.totalIcms ?? 0) / (s?.totalValue ?? 1) * p.totalValue : null,      { direction: "neutral" });
  const mercDelta      = computeDelta(s?.goodsValue,      p?.goodsValue,      { direction: "higher_is_better" });
  const quantidadeDelta = computeDelta(s?.totalQuantity,  p?.totalQuantity,   { direction: "higher_is_better" });
  const pesoDelta       = computeDelta(s?.totalWeightKg,  p?.totalWeightKg,   { direction: "higher_is_better" });

  const byDay       = cte?.byDay       ?? [];
  const ticketTrend = cte?.ticketTrend ?? [];
  const topCustomers = cte?.topCustomers ?? [];
  const topVehicles  = cte?.topVehicles  ?? [];
  const customerList = useProgressiveList(topCustomers);
  const vehicleList  = useProgressiveList(topVehicles);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Operacional CT-e"
        subtitle="Emissão, clientes, veículos e análise do faturamento por frete"
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        <MemoryCardWrapper cardKey="cte-receita-mes" navigate={navigate}>
          <KPIStatCard
            label="Receita CT-e"
            value={s ? fmtBRL(s.totalValue) : "—"}
            delta={receitaDelta}
            comparisonLabel={cte?.comparisonLabel ?? "vs. mês anterior"}
            status={s ? (s.totalValue > 0 ? "good" : "neutral") : "neutral"}
            loading={isLoading}
          />
        </MemoryCardWrapper>
        <MemoryCardWrapper cardKey="cte-count-mes" navigate={navigate}>
          <KPIStatCard
            label="CT-es emitidos"
            value={s ? fmtNumber(s.count) : "—"}
            delta={countDelta}
            comparisonLabel={cte?.comparisonLabel ?? "vs. mês anterior"}
            status={s ? (s.count > 0 ? "good" : "neutral") : "neutral"}
            loading={isLoading}
          />
        </MemoryCardWrapper>
        <MemoryCardWrapper cardKey="cte-mercadoria-mes" navigate={navigate}>
          <KPIStatCard
            label="Mercadoria transportada"
            value={s ? fmtBRL(s.goodsValue) : "—"}
            delta={mercDelta}
            comparisonLabel={cte?.comparisonLabel ?? "vs. mês anterior"}
            status={s ? (s.goodsValue > 0 ? "good" : "neutral") : "neutral"}
            loading={isLoading}
          />
        </MemoryCardWrapper>
        <MemoryCardWrapper cardKey="cte-icms-mes" navigate={navigate}>
          <KPIStatCard
            label="ICMS"
            value={s ? fmtBRL(s.totalIcms) : "—"}
            delta={icmsDelta}
            comparisonLabel={cte?.comparisonLabel ?? "vs. mês anterior"}
            status="neutral"
            loading={isLoading}
          />
        </MemoryCardWrapper>
        <MemoryCardWrapper cardKey="cte-pedagio-mes" navigate={navigate}>
          <KPIStatCard
            label="Pedágios"
            value={s ? fmtBRL(s.tollValue) : "—"}
            delta={pedagioDelta}
            comparisonLabel={cte?.comparisonLabel ?? "vs. mês anterior"}
            status={s && s.tollValue > 0 ? "warning" : "neutral"}
            loading={isLoading}
          />
        </MemoryCardWrapper>
        <MemoryCardWrapper cardKey="cte-quantidade-mes" navigate={navigate}>
          <KPIStatCard
            label="Qtde transportada"
            value={s ? fmtNumber(Math.round(s.totalQuantity)) : "—"}
            delta={quantidadeDelta}
            comparisonLabel={cte?.comparisonLabel ?? "vs. mês anterior"}
            status={s ? (s.totalQuantity > 0 ? "good" : "neutral") : "neutral"}
            loading={isLoading}
          />
        </MemoryCardWrapper>
        <MemoryCardWrapper cardKey="cte-peso-mes" navigate={navigate}>
          <KPIStatCard
            label="Peso total (Kg)"
            value={s ? fmtNumber(Math.round(s.totalWeightKg)) : "—"}
            delta={pesoDelta}
            comparisonLabel={cte?.comparisonLabel ?? "vs. mês anterior"}
            status={s ? (s.totalWeightKg > 0 ? "good" : "neutral") : "neutral"}
            loading={isLoading}
          />
        </MemoryCardWrapper>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="CT-es por dia" subtitle="Volume — mês atual" height={240}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={byDay} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-cte" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="var(--brand)" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="var(--brand)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tickFormatter={fmtDateShort}
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <Tooltip
                formatter={(v: number, n: string) => [
                  n === "count" ? fmtNumber(v) : fmtBRL(v),
                  n === "count" ? "CT-es" : "Valor",
                ]}
                labelFormatter={fmtDateShort}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke="var(--brand)"
                strokeWidth={2}
                fill="url(#grad-cte)"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Evolução do Ticket Médio"
          subtitle="Por mês — últimos 5 meses"
          height={240}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={ticketTrend} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tickFormatter={(v) => `R$ ${(v / 1000).toFixed(1)}k`}
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                formatter={(v: number) => [fmtBRL(v), "Ticket médio"]}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }}
              />
              <Bar dataKey="avgTicket" fill="var(--brand)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Top clients + vehicles */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top clients */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900">Top Clientes por Faturamento</p>
          </div>
          <div className="divide-y divide-surface-border">
            {isLoading ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Carregando…</p>
            ) : topCustomers.length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Sem dados no período.</p>
            ) : customerList.visible.map((c, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50"
                title="Ver CT-es que compõem o faturamento (memória de cálculo)"
                onClick={() => navigate(`/operacional/memoria/cte-receita-mes?periodFrom=${encodeURIComponent(from)}&periodTo=${encodeURIComponent(to)}`)}>
                <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{c.name}</p>
                  <p className="text-xs text-gray-400">{fmtNumber(c.cteCount)} CT-es</p>
                </div>
                <p className="text-sm font-semibold text-tm-700 flex-shrink-0">{c.formatted}</p>
              </div>
            ))}
            {!isLoading && <LoadMoreBar list={customerList} label="clientes" />}
          </div>
        </div>

        {/* Top vehicles */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <IconTruck className="w-4 h-4 text-gray-400" />
              Receita por Veículo
            </p>
          </div>
          <div className="divide-y divide-surface-border">
            {isLoading ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Carregando…</p>
            ) : topVehicles.length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Sem dados no período.</p>
            ) : vehicleList.visible.map((v, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50"
                title="Abrir o raio-X da placa"
                onClick={() => navigate(`/frota/placa/${encodeURIComponent(v.plate)}`)}>
                <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-tm-700">{v.plate}</p>
                  <p className="text-xs text-gray-400">
                    {[v.brand, v.modelYear, `${fmtNumber(v.cteCount)} CT-es`].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <p className="text-sm font-semibold text-gray-900">{v.formatted}</p>
              </div>
            ))}
            {!isLoading && <LoadMoreBar list={vehicleList} label="veículos" />}
          </div>
        </div>
      </div>
    </div>
  );
}
